import { useCallback } from "react";
import { Capacitor } from "@capacitor/core";

type ImpactStyle = "light" | "medium" | "heavy";
type NotificationType = "success" | "warning" | "error";

async function getHaptics() {
  if (!Capacitor.isNativePlatform()) return null;
  try {
    const { Haptics } = await import("@capacitor/haptics");
    return Haptics;
  } catch {
    return null;
  }
}

/**
 * Wrapper around @capacitor/haptics.
 * Silently no-ops on web.
 */
export function useHaptics() {
  const impact = useCallback(async (style: ImpactStyle = "light") => {
    const haptics = await getHaptics();
    if (!haptics) return;

    const styleMap = {
      light: "LIGHT",
      medium: "MEDIUM",
      heavy: "HEAVY",
    } as const;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await haptics.impact({ style: styleMap[style] as any });
  }, []);

  const notification = useCallback(async (type: NotificationType = "success") => {
    const haptics = await getHaptics();
    if (!haptics) return;

    const typeMap = {
      success: "SUCCESS",
      warning: "WARNING",
      error: "ERROR",
    } as const;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await haptics.notification({ type: typeMap[type] as any });
  }, []);

  const selection = useCallback(async () => {
    const haptics = await getHaptics();
    if (!haptics) return;
    await haptics.selectionStart();
  }, []);

  return { impact, notification, selection };
}
